import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Download, Lock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ReportView from "./ReportView";
import { exportNodeToPdfPaged } from "../lib/pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function EmailGate({ gate, email, setEmail, onSubmit, busy, denied }) {
  return (
    <div className="min-h-screen bg-brand-page flex items-center justify-center px-6" data-testid="report-gate">
      <form onSubmit={onSubmit} className="w-full max-w-md bg-brand-surface border border-brand-border p-8 sm:p-10">
        <Lock className="h-5 w-5 text-brand-gold" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary mt-5">Shared Report · {gate.period_label}</p>
        <h1 className="font-serif text-3xl text-brand-ink mt-2" data-testid="report-gate-title">{gate.title}</h1>
        <p className="text-sm text-brand-muted mt-3">
Please confirm your email to view this report.
        </p>
        {denied && <p className="mt-4 text-sm text-brand-neg border border-brand-neg/30 bg-brand-neg/5 px-3 py-2.5" data-testid="report-gate-denied">{denied}</p>}
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
          data-testid="report-gate-email" className="mt-6 w-full bg-brand-page border border-brand-border px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
        <button type="submit" disabled={busy} data-testid="report-gate-submit"
          className="mt-4 w-full bg-brand-ink text-brand-page py-2.5 text-sm hover:bg-brand-primary active:scale-[0.98] transition-all disabled:opacity-60">
          {busy ? "Opening…" : "View report"}
        </button>
      </form>
    </div>
  );
}

export default function ReportPublic() {
  const { token } = useParams();
  const storeKey = `bond-report-email-${token}`;
  const [gate, setGate] = useState(null);
  const [report, setReport] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const viewRef = useRef(null);

  const open = useCallback(async (addr) => {
    setBusy(true); setDenied(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/reports/${token}/open`, { email: addr }, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      localStorage.setItem(storeKey, addr);
      setReport(data);
    } catch (e) {
      if (e?.response?.status === 403) { localStorage.removeItem(storeKey); setDenied(e.response.data.detail); }
      else toast.error(e?.response?.data?.detail || "Could not open the report.");
    } finally { setBusy(false); }
  }, [token, storeKey]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/reports/${token}`)
      .then(({ data }) => {
        setGate(data);
        const saved = localStorage.getItem(storeKey);
        setEmail(saved || "");
        if (saved) open(saved);
      })
      .catch(() => setError("This report is not available. It may have been unpublished or the link is incorrect."));
  }, [token, storeKey, open]);

  const download = async () => {
    setDownloading(true);
    try {
      await exportNodeToPdfPaged(viewRef.current, `${(report.title || "report").replace(/[^a-z0-9]+/gi, "-")}.pdf`);
    } catch { toast.error("Could not generate the PDF."); }
    finally { setDownloading(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-brand-page flex items-center justify-center px-6">
        <div className="text-center" data-testid="report-unavailable">
          <p className="font-serif text-3xl text-brand-ink">Report unavailable</p>
          <p className="text-sm text-brand-muted mt-3 max-w-md">{error}</p>
        </div>
      </div>
    );
  }
  if (!gate || (busy && !report && localStorage.getItem(storeKey))) {
    return <div className="min-h-screen bg-brand-page flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>;
  }
  if (!report) {
    return <EmailGate gate={gate} denied={denied} email={email} setEmail={setEmail} busy={busy} onSubmit={(e) => { e.preventDefault(); open(email.trim()); }} />;
  }

  return (
    <div className="min-h-screen bg-brand-page">
      <div className="sticky top-0 z-20 bg-brand-page/85 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-[880px] mx-auto px-8 sm:px-14 h-14 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">Shared Report</span>
          <button onClick={download} disabled={downloading} data-testid="public-download-pdf"
            className="inline-flex items-center gap-2 text-sm bg-brand-ink text-brand-page px-4 py-2 hover:bg-brand-primary active:scale-[0.98] transition-all disabled:opacity-60">
            <Download className="h-4 w-4" /> {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>
      <ReportView ref={viewRef} report={report} />
    </div>
  );
}
